import { useInstance } from '@meraki-internal/react-dependency-injection';
import { AlertPresenter } from '../AlertBinder';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { CareMapState } from '../caremap/CareMapState';
import { NewCareMapWizard } from '../wizard/mini-wizard/wizards/NewCareMapWizard';
import { useEffect, useMemo, useState } from 'react';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { useSubscription } from '@meraki-internal/state';
import { HeaderButton } from '../components/HeaderButton';
import { NavigationItemList } from './NavigationItemList';
import { CaremapSwitcher } from './CaremapSwitcher';
import { NavigationItem } from './NavigationItem';
import { AddCareMapButton } from '../caremap/AddCareMapButton';
import { AppPrincipal, isCaremapViewer } from '../auth/AppPrincipal';
import { InnerhiveSVGIcon } from '../theme/Icons';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';

interface NewUINavigationListProps {
    iconsOnly?: boolean;
    closeMenuHandler?: () => void;
    popoverWidth?: number;
}


interface INavConfig {
    id: string;
    icon: React.ReactNode;
    label: string;
    onClick?: () => void;
    isSelected?: (path: string) => boolean;
    isDisabled?: () => boolean;
}

export const NewUINavigationList: React.FC<NewUINavigationListProps> = ({ iconsOnly, closeMenuHandler, popoverWidth  }) => {
    const alert = useInstance(AlertPresenter);
    const history = useInstance(HistoryViewModel);
    const careMapState = useInstance(CareMapState);
    const newCareMapWizard = useInstance(NewCareMapWizard);
    const mixpanel = useInstance(MixPanelEventEmitter);

    const isViewer = isCaremapViewer(useInstance(AppPrincipal));

    const [selectedNavLabel, setSelectedNavLabel] = useState('');

    const orgState = useInstance(OrganizationsState);
    useSubscription(() => orgState);

    const gotoPathIfPossible = (path: string) => {
        let isPossible: boolean;

        if (!orgState.isOrgTeamMember() && careMapState.getCareMaps().length === 0){
            newCareMapWizard.start();
            isPossible = false;
        } else {
            history.push(path);
            isPossible = true;
        }

        return isPossible;
    };

    let nav = useMemo<INavConfig[]>(() => {
        return [
            {
                id: 'caregiver',
                label: `Home`,
                icon: <svg xmlns="http://www.w3.org/2000/svg" width="30" fill="var(--ion-color-primary)" viewBox="0 0 576 512">
                    <path d="M163.9 136.9c-29.4-29.8-29.4-78.2 0-108s77-29.8 106.4 0l17.7 18 17.7-18c29.4-29.8 77-29.8 106.4 0s29.4 78.2 0 108L310.5 240.1c-6.2 6.3-14.3 9.4-22.5 9.4s-16.3-3.1-22.5-9.4L163.9 136.9zM187.4 352c-7.9 0-15.6 2.3-22.2 6.7L85.3 412c-3.9 2.6-8.6 4-13.3 4H24c-13.3 0-24-10.7-24-24s10.7-24 24-24H64.7l73.8-49.2C153 309.1 170 304 187.4 304H344c35.3 0 64 28.7 64 64c0 .7 0 1.3 0 2l64.9-40c10.7-6.6 22.9-10 35.5-10h7.4c33.3 0 60.3 27 60.3 60.3c0 20.4-10.4 39.5-27.5 50.6l-95.6 61.9c-19.4 12.6-42.1 19.3-65.2 19.3H24c-13.3 0-24-10.7-24-24s10.7-24 24-24H387.7c13.9 0 27.5-4 39.1-11.6l95.6-61.9c3.5-2.3 5.6-6.1 5.6-10.3c0-6.8-5.5-12.3-12.3-12.3h-7.4c-3.6 0-7.2 1-10.3 2.9l-93.5 57.5c-3.8 2.3-8.1 3.6-12.6 3.6H344 320 256c-13.3 0-24-10.7-24-24s10.7-24 24-24h64 24c8.8 0 16-7.2 16-16s-7.2-16-16-16H187.4z"/>
                </svg>,
                onClick: () => {
                    history.push('/caregiver');
                },
                isSelected: path => path === '/caregiver',
                isDisabled: () => orgState.isOrgTeamMember() || isViewer
            },
            {
                id: 'care',
                label: `Care Profile`,
                icon: <svg xmlns="http://www.w3.org/2000/svg" width="25" viewBox="0 0 512 512"><path d="M184 48l144 0c4.4 0 8 3.6 8 8l0 40L176 96l0-40c0-4.4 3.6-8 8-8zm-56 8l0 40L64 96C28.7 96 0 124.7 0 160L0 416c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-256c0-35.3-28.7-64-64-64l-64 0 0-40c0-30.9-25.1-56-56-56L184 0c-30.9 0-56 25.1-56 56zm24 88l208 0 88 0c8.8 0 16 7.2 16 16l0 256c0 8.8-7.2 16-16 16L64 432c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l88 0zm72 64l0 48-48 0c-8.8 0-16 7.2-16 16l0 32c0 8.8 7.2 16 16 16l48 0 0 48c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-48 48 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-48 0 0-48c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16z"/></svg>,
                onClick: () => {
                    if (gotoPathIfPossible(`/care/${careMapState.getMostRecentCaremap()?.careMapId}`)) {
                        mixpanel.track('Care Page Opened', () => ({
                            'Opened From': 'Menu'
                        }));
                    }
                },
                isSelected: path => path.startsWith('/care/')
            },
            {
                id: 'caremap',
                label: `Care Map`,
                icon: InnerhiveSVGIcon,
                onClick: () => gotoPathIfPossible(`/caremaps/${careMapState.getMostRecentCaremap()?.careMapId}`),
                isSelected: path => path.startsWith('/caremaps/')
            },
            {
                id: 'community',
                label: `Community`,
                icon: <svg xmlns="http://www.w3.org/2000/svg" width="30" viewBox="0 0 640 512"><path opacity=".4" d="M0 304c0 44.7 26.2 83.2 64 101.2L64 448c0 17.7 14.3 32 32 32l64 0c17.7 0 32-14.3 32-32l0-42.8 0-10.7c-20-24.7-32-56.2-32-90.5c0-37.8 14.6-72.2 38.4-97.9c-16.1-9-34.7-14.1-54.4-14.1l-32 0C50.1 192 0 242.1 0 304zm48 0c0-16.2 6-31.1 16-42.3l0 84.7C54 335 48 320.2 48 304zM64 96a64 64 0 1 0 128 0A64 64 0 1 0 64 96zM441.6 206.1C465.4 231.8 480 266.2 480 304c0 34.3-12 65.8-32 90.5l0 10.7 0 42.8c0 17.7 14.3 32 32 32l64 0c17.7 0 32-14.3 32-32l0-42.8c37.8-18 64-56.5 64-101.2c0-61.9-50.1-112-112-112l-32 0c-19.7 0-38.3 5.1-54.4 14.1zM448 96a64 64 0 1 0 128 0A64 64 0 1 0 448 96zM576 261.7c10 11.3 16 26.1 16 42.3s-6 31-16 42.3l0-84.7z"/><path d="M384 96A64 64 0 1 0 256 96a64 64 0 1 0 128 0zM256 261.7l0 84.7c-10-11.3-16-26.1-16-42.3s6-31.1 16-42.3zm128 84.7l0-84.7c10 11.3 16 26.1 16 42.3s-6 31-16 42.3zm0 58.9c37.8-18 64-56.5 64-101.2c0-61.9-50.1-112-112-112l-32 0c-61.9 0-112 50.1-112 112c0 44.7 26.2 83.2 64 101.2l0 42.8c0 17.7 14.3 32 32 32l64 0c17.7 0 32-14.3 32-32l0-42.8z"/></svg>,
                onClick: () => gotoPathIfPossible(`/community/${careMapState.getMostRecentCaremap()?.careMapId}/feed`),
                isSelected: path => path.startsWith('/community/')
            },
            {
                id: 'notes',
                label: `Notes`,
                icon: <svg xmlns="http://www.w3.org/2000/svg" width="23" viewBox="0 0 384 512"><path opacity=".4" d="M0 64C0 28.7 28.7 0 64 0L224 0l0 128c0 17.7 14.3 32 32 32l128 0 0 288c0 35.3-28.7 64-64 64L64 512c-35.3 0-64-28.7-64-64L0 64zM96 272c0 8.8 7.2 16 16 16l160 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-160 0c-8.8 0-16 7.2-16 16zm0 64c0 8.8 7.2 16 16 16l160 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-160 0c-8.8 0-16 7.2-16 16zm0 64c0 8.8 7.2 16 16 16l160 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-160 0c-8.8 0-16 7.2-16 16z"/><path d="M384 160L224 0l0 128c0 17.7 14.3 32 32 32l128 0zM112 256c-8.8 0-16 7.2-16 16s7.2 16 16 16l160 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-160 0zm0 64c-8.8 0-16 7.2-16 16s7.2 16 16 16l160 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-160 0zm0 64c-8.8 0-16 7.2-16 16s7.2 16 16 16l160 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-160 0z"/></svg>,
                onClick: () => gotoPathIfPossible(`/notes/${careMapState.getMostRecentCaremap()?.careMapId}`),
                isSelected: path => path.startsWith('/notes/')
            },
            {
                id: 'appointments',
                label: `Appointments`,
                icon: <svg xmlns="http://www.w3.org/2000/svg" width="25" viewBox="0 0 448 512"><path opacity=".4" d="M0 192l448 0 0 272c0 26.5-21.5 48-48 48L48 512c-26.5 0-48-21.5-48-48L0 192zm64 80l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zm0 128l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zM192 272l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zm0 128l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zM320 272l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zm0 128l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16z"/><path d="M160 32c0-17.7-14.3-32-32-32S96 14.3 96 32l0 32L48 64C21.5 64 0 85.5 0 112l0 80 448 0 0-80c0-26.5-21.5-48-48-48l-48 0 0-32c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 32L160 64l0-32z"/></svg>,
                onClick: () => gotoPathIfPossible(`/appointments/${careMapState.getMostRecentCaremap()?.careMapId}`),
                isSelected: path => path.startsWith('/appointments/')
            }
        ];
        // don't want to pass in gotoPathIfPossible
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [careMapState, newCareMapWizard, history, orgState, isViewer]);

    useEffect(() => {
        const checkForSelectedNavItem = () =>{
            const path = history.getCurrentLocation().pathname;
            setSelectedNavLabel(nav.find(n => n.isSelected && n.isSelected(path))?.label || '');
        };

        checkForSelectedNavItem();
        return history.listen(checkForSelectedNavItem);

    }, [history, nav]);

    const handleClick = async (navItem: INavConfig) => {
        if(closeMenuHandler){
            closeMenuHandler();
        }
        await new Promise(resolve => setTimeout(resolve, 350));
        if (!navItem.onClick){
            alert.showAlertV2({ header: navItem.label, message: 'Coming Soon'});
        }
        else {
            navItem.onClick();
        }
    };

    nav = nav.filter(n => !n.isDisabled || !n.isDisabled());

    if (iconsOnly){
        nav = nav.filter(i => i.onClick);
    }

    // don't show nav, that would direct user to create a personal caremap
    if (orgState.isOrgTeamMember() && !careMapState.getMostRecentCaremap()){
        return null;
    }

    if (iconsOnly){
        return (
            <>
                {nav.map((item, i) => (
                    <HeaderButton key={item.id} id={item.id}  onClick={() => handleClick(item)}>
                        <div style={{marginTop: 2}}>
                            {item.icon}
                        </div>
                    </HeaderButton>
                ))}
            </>
        );
    }

    const showAddButton = !orgState.isOrgTeamMember() && !isViewer;

    return (
        <NavigationItemList>
            {popoverWidth && !orgState.isOrgTeamMember() && (
                <CaremapSwitcher
                    popoverWidth={popoverWidth}
                    closeNav={async () => {
                        if (closeMenuHandler){
                            closeMenuHandler();
                            await new Promise(resolve => setTimeout(resolve, 350));
                        }
                    }}
                />
            )}

            <div style={{ marginTop: 15}} />
            {nav.map(item => (
                <NavigationItem
                    key={item.id}
                    data-type={`${item.id}-nav-item`}
                    label={item.label}
                    disabled={!item.onClick}
                    onClick={() => handleClick(item)}
                    isSelected={selectedNavLabel === item.label}
                    icon={item.icon}
                />
            ))}
            {showAddButton && (
                <div style={{margin: 24}}>
                    <AddCareMapButton closeMenu={() => {
                        if (closeMenuHandler){
                            closeMenuHandler();
                        }
                    }} />
                </div>
            )}
        </NavigationItemList>
    );
};


